import { observable } from "@legendapp/state";
import { getArrivalPoiReport } from "@/apis/clients/arrivalClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { ArrivalReportRow, ReportScope } from "@/types/reporting";

interface ArrivalPoiState {
  rows: ArrivalReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: ArrivalPoiState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const ArrivalPoiStore$ = observable(defaultValues);

const getScopeKey = (scope: ReportScope) =>
  `arrival-poi:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchArrivalPoiReport = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    ArrivalPoiStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    ArrivalPoiStore$.assign({ isLoading: false, error: null });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  ArrivalPoiStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getArrivalPoiReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ArrivalPoiStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ArrivalPoiStore$.assign({
      error: toErrorMessage(error),
      loadedForScopeKey: null,
      rows: [],
    });
  } finally {
    if (!guard.isStale(requestId)) {
      ArrivalPoiStore$.isLoading.set(false);
    }
  }
};

export const clearArrivalPoiReport = () => {
  guard.invalidate();
  ArrivalPoiStore$.assign({
    error: null,
    isLoading: false,
    loadedForScopeKey: null,
    rows: [],
  });
};
